import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Fab,
  IconButton,
  Grid,
} from "@mui/material";
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Add as AddIcon,
  AccountBalanceWalletOutlined as WalletIcon,
  PaidOutlined as PaidIcon,
  EventBusyOutlined as UnpaidIcon,
  LoopOutlined as CarryIcon,
} from "@mui/icons-material";
import { BRAND } from "../../components/auth/authTheme";
import MobileMonthYearFilters from "../../components/mobile/MobileMonthYearFilters";

const NAVY = BRAND.navy;
const ORANGE = BRAND.orangeHover;
const BG = "#F8FAFC";

const StatCard = ({ icon, label, value, iconBg, iconColor }) => (
  <Box
    sx={{
      bgcolor: "#fff",
      borderRadius: "12px",
      px: 0.75,
      py: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      gap: 0.5,
      boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
      border: `1px solid ${BRAND.border}`,
      height: "100%",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        width: 30,
        height: 30,
        borderRadius: "8px",
        bgcolor: iconBg,
        color: iconColor,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
    <Typography
      sx={{
        fontSize: "1rem",
        fontWeight: 700,
        color: NAVY,
        lineHeight: 1,
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.25,
        whiteSpace: "normal",
        wordBreak: "break-word",
        width: "100%",
      }}
    >
      {label}
    </Typography>
  </Box>
);

const BalanceChip = ({ label, value }) => (
  <Box
    sx={{
      bgcolor: BRAND.bg,
      borderRadius: "8px",
      px: 1,
      py: 0.75,
      border: `1px solid ${BRAND.border}`,
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontSize: "0.625rem",
        color: BRAND.muted,
        fontWeight: 500,
        lineHeight: 1.2,
        mb: 0.25,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontSize: "0.8125rem",
        fontWeight: 700,
        color: NAVY,
        lineHeight: 1.2,
      }}
    >
      {value}
    </Typography>
  </Box>
);

const LeaveBalanceCard = ({ record, onEdit }) => {
  const emp = record.employee || {};
  const fullName = `${emp.first_name || ""} ${emp.last_name || ""}`.trim();

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        borderRadius: "12px",
        mb: 1.25,
        border: `1px solid ${BRAND.border}`,
        boxShadow: "0 1px 6px rgba(26, 43, 74, 0.04)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ px: 1.25, py: 0.875 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 32px",
            alignItems: "center",
            columnGap: 0.75,
            mb: 0.35,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: NAVY,
              fontSize: "0.875rem",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.25,
            }}
          >
            {fullName || "Unknown"}
          </Typography>
          <IconButton
            size="small"
            onClick={() => onEdit(record)}
            aria-label={`Edit ${fullName}`}
            sx={{
              p: 0,
              color: ORANGE,
              bgcolor: BRAND.orangeLight,
              width: 32,
              height: 32,
            }}
          >
            <EditIcon sx={{ fontSize: "1.05rem" }} />
          </IconButton>
        </Box>

        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 600,
            color: ORANGE,
            lineHeight: 1.2,
            mb: 0.75,
          }}
        >
          {emp.employee_id || "N/A"} · {record.leave_year}
        </Typography>

        <Grid container spacing={0.75}>
          <Grid item xs={6}>
            <BalanceChip
              label="Paid"
              value={`${record.remaining_paid_leave_balance ?? 0} / ${record.paid_leave_balance ?? 0}`}
            />
          </Grid>
          <Grid item xs={6}>
            <BalanceChip
              label="Unpaid"
              value={`${record.remaining_unpaid_leave_balance ?? 0} / ${record.unpaid_leave_balance ?? 0}`}
            />
          </Grid>
          <Grid item xs={6}>
            <BalanceChip
              label="Carry Fwd"
              value={record.carry_forward_days ?? 0}
            />
          </Grid>
          <Grid item xs={6}>
            <BalanceChip
              label="Total"
              value={`${record.remaining_total_leave_balance ?? 0} / ${record.total_leave_balance ?? 0}`}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

const LeaveBalanceMobileView = ({
  records,
  searchTerm,
  setSearchTerm,
  selectedYear,
  setSelectedYear,
  yearOptions,
  onEdit,
  onAdd,
}) => {
  const filteredList = useMemo(() => {
    if (!searchTerm.trim()) return records;
    const q = searchTerm.toLowerCase();
    return records.filter((row) => {
      const emp = row.employee || {};
      const name = `${emp.first_name || ""} ${emp.last_name || ""}`.toLowerCase();
      return (
        name.includes(q) ||
        emp.employee_id?.toLowerCase().includes(q)
      );
    });
  }, [records, searchTerm]);

  const stats = useMemo(() => {
    const totalPaid = records.reduce(
      (sum, r) => sum + Number(r.paid_leave_balance || 0),
      0
    );
    const totalRemaining = records.reduce(
      (sum, r) => sum + Number(r.remaining_total_leave_balance || 0),
      0
    );
    return {
      employees: records.length,
      totalPaid,
      totalRemaining,
    };
  }, [records]);

  return (
    <Box sx={{ pb: 10, pt: 1, px: 2, bgcolor: BG, minHeight: "100vh" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 1,
          mb: 2,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: NAVY }}>
            Leave Balance
          </Typography>
          <Typography sx={{ fontSize: "0.8125rem", color: BRAND.muted, mt: 0.25 }}>
            Allocations for {selectedYear}
          </Typography>
        </Box>
        <MobileMonthYearFilters
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
          yearOptions={yearOptions}
          showMonth={false}
        />
      </Box>

      <Grid container spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={4} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<WalletIcon sx={{ fontSize: "1rem" }} />}
            label="Employees"
            value={stats.employees}
            iconBg="rgba(33,150,243,0.15)"
            iconColor="#2196f3"
          />
        </Grid>
        <Grid item xs={4} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<PaidIcon sx={{ fontSize: "1rem" }} />}
            label="Paid Days"
            value={stats.totalPaid}
            iconBg="rgba(76,175,80,0.15)"
            iconColor="#4caf50"
          />
        </Grid>
        <Grid item xs={4} sx={{ minWidth: 0 }}>
          <StatCard
            icon={<UnpaidIcon sx={{ fontSize: "1rem" }} />}
            label="Remaining"
            value={stats.totalRemaining}
            iconBg="rgba(255,152,0,0.15)"
            iconColor="#ff9800"
          />
        </Grid>
      </Grid>

      <TextField
        fullWidth
        placeholder="Search employees..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{
          mb: 2,
          "& .MuiOutlinedInput-root": {
            bgcolor: "#fff",
            borderRadius: "12px",
            "& fieldset": { borderColor: BRAND.border },
            "&.Mui-focused fieldset": { borderColor: ORANGE },
          },
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: BRAND.muted }} />
            </InputAdornment>
          ),
        }}
      />

      {filteredList.length > 0 ? (
        filteredList.map((record) => (
          <LeaveBalanceCard key={record.id} record={record} onEdit={onEdit} />
        ))
      ) : (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "#fff",
            borderRadius: "16px",
            border: `1px solid ${BRAND.border}`,
          }}
        >
          <CarryIcon sx={{ fontSize: 48, color: "#CBD5E1", mb: 1 }} />
          <Typography sx={{ fontWeight: 600, color: NAVY, mb: 0.5 }}>
            No leave balances found
          </Typography>
          <Typography sx={{ fontSize: "0.875rem", color: BRAND.muted }}>
            No records for {selectedYear}. Tap + to add one.
          </Typography>
        </Box>
      )}

      <Fab
        color="primary"
        aria-label="Manage leave balance"
        onClick={onAdd}
        size="medium"
        sx={{
          position: "fixed",
          bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
          right: 20,
          bgcolor: ORANGE,
          "&:hover": { bgcolor: BRAND.orange },
          boxShadow: "0 4px 16px rgba(230, 90, 0, 0.35)",
          zIndex: 1002,
        }}
      >
        <AddIcon />
      </Fab>
    </Box>
  );
};

export default LeaveBalanceMobileView;
